import styled from "@emotion/styled";
import { SelectTokenRowTooltip } from "components/TokenSelector/elements/SelectTokenRowTooltip";
import { SkeletonBlock, SkeletonCircle } from "components/general/Skeleton";
import { Text } from "components/general/Text";
import { useGetTokenRowDisabledReason } from "hooks/trade/useGetTokenRowDisabledReason";
import { useIsDesktop } from "hooks/useDeviceType";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { Token } from "store/global/TokenEnum";
import { TokenList } from "store/global/getAllTokenLists";
import { Amount } from "types/amount";
import { CounterParty } from "utils/tradeUtils";
import { useTokenlistMetadataByChain } from "hooks/queries/tokens-queries";
import { getTokenAddressBySymbol } from "utils/orderUtils";
import { getTokenIcon } from "utils/tokenIconUtils";
import { useHover } from "usehooks-ts";
import { css } from "@emotion/react";
import Decimal from "decimal.js";
import { changeTokenOnStore } from "../../../utils/tokenSelectorUtils";

interface TokenSelectorRowProps {
  symbol: Token;
  counterParty: CounterParty;
  showSeparatorAbove: boolean;
  addTokenToStore: () => void;
  closeDropdown: () => void;
  confirmHighlightedRowIdx: () => void;
  dropdownBoundingClientRect: DOMRect | undefined;
  scrollToTop: () => void;
  isFirst: boolean;
  isLastHighlightedWithKeyboard: boolean;
  style?: CSSProperties;
  isLoading?: boolean;
  isHighlighted?: boolean;
  isChangingToken: Token | undefined;
}

export const TokenSelectorRow = ({
  symbol,
  counterParty,
  showSeparatorAbove,
  addTokenToStore,
  closeDropdown,
  confirmHighlightedRowIdx,
  scrollToTop,
  dropdownBoundingClientRect,
  isFirst,
  isLastHighlightedWithKeyboard,
  style,
  isLoading = false,
  isHighlighted = false,
  isChangingToken,
}: TokenSelectorRowProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const isHovered = useHover(ref);
  const tokenList = useTokenListByChain();
  const balances = GlobalStore.useState(s => s.balances);
  const balance = balances.find(entry => entry.symbol === symbol)?.balance;
  const displayBalance = useMemo(
    () => getDisplayBalance(balance, symbol, tokenList),
    [balance, symbol, tokenList]
  );
  const tokenPrice = tokenList?.[symbol]?.priceUsd ?? Amount.ZERO;
  const balanceUsd = balance ? tokenPrice.value.mul(balance.value) : Amount.ZERO.value;
  const theme = GlobalStore.useState(s => s.theme);
  const tokenRowDisabledReason = useGetTokenRowDisabledReason()(
    symbol,
    counterParty,
    isChangingToken
  );

  const isDisabled = tokenRowDisabledReason != null;

  const currBoundingClientRect = ref.current?.getBoundingClientRect();
  const isRowFullyVisible =
    currBoundingClientRect != null &&
    dropdownBoundingClientRect != null &&
    currBoundingClientRect.top >= dropdownBoundingClientRect?.top &&
    currBoundingClientRect?.bottom <= dropdownBoundingClientRect?.bottom &&
    currBoundingClientRect?.left >= dropdownBoundingClientRect?.left &&
    currBoundingClientRect?.right <= dropdownBoundingClientRect?.right;

  if (isHighlighted && isLastHighlightedWithKeyboard && !isRowFullyVisible) {
    if (isFirst) {
      scrollToTop();
    } else {
      ref.current?.scrollIntoView();
    }
  }

  useEffect(() => {
    if (isHovered) {
      confirmHighlightedRowIdx();
    }
  }, [isHighlighted, isHovered, confirmHighlightedRowIdx]);

  const MainDetails = useCallback(() => {
    const TokenName = () => {
      if (isLoading) {
        return (
          <div style={{ marginBottom: 3 }}>
            <SkeletonBlock width={94} height={14} />
          </div>
        );
      }
      return (
        <Text as="p" textStyle="Title" size="Small" shouldScale={true}>
          {tokenList?.[symbol]?.name}
        </Text>
      );
    };

    const BalanceDetails = () => {
      if (isLoading) {
        return <SkeletonBlock width={54} height={12} />;
      }

      if (balance != null && balance.value.greaterThan(0)) {
        return (
          <BalanceDetailsContainer>
            <Text textStyle="Paragraph" size="Large" shouldScale={true}>
              {displayBalance}
            </Text>
          </BalanceDetailsContainer>
        );
      }

      return <BalanceDetailsContainer>{symbol}</BalanceDetailsContainer>;
    };

    return (
      <SubContainer>
        <TokenName />
        <BalanceDetails />
      </SubContainer>
    );
  }, [tokenList, symbol, balance, isLoading, displayBalance]);

  if (tokenList != null && !(symbol in tokenList)) {
    return null;
  }

  const content = (
    <SelectTokenRowTooltip
      tokenDisabledReason={tokenRowDisabledReason}
      counterParty={counterParty}
      symbol={symbol}
    >
      <Container
        ref={ref}
        style={showSeparatorAbove ? undefined : style}
        isLoading={isLoading}
        isHighlighted={isHighlighted}
        isDisabled={isDisabled}
        onClick={() => {
          if (isDisabled) return;
          if (isChangingToken) {
            void changeTokenOnStore(counterParty, isChangingToken, symbol);
          } else {
            addTokenToStore();
          }
          closeDropdown();
        }}
      >
        <TokenIcon symbol={symbol} isLoading={isLoading} />
        <MainDetails />
        <TokenStats symbol={symbol} isLoading={isLoading} balanceUsd={balanceUsd} />
      </Container>
    </SelectTokenRowTooltip>
  );

  if (showSeparatorAbove) {
    return (
      <div style={{ ...style, width: "100%" }}>
        {showSeparatorAbove && (
          <div
            style={{
              marginTop: 13,
              marginBottom: 13,
              height: 1,
              width: "100%",
              backgroundColor: theme.button.secondary.text.active,
            }}
          />
        )}
        {content}
      </div>
    );
  }

  return <>{content}</>;
};

// Sub-components

const Container = styled.button<{
  isLoading: boolean;
  isHighlighted: boolean;
  isDisabled: boolean;
}>(({ theme, isLoading, isHighlighted, isDisabled }) => {
  return {
    display: "flex",
    padding: "12px 8px 12px 12px",
    borderRadius: 18,
    alignItems: "center",
    background: isHighlighted ? theme.surface.main.secondary.hover : "none",
    border: "none",
    transition: "background 0.1s linear",
    color: theme.font.primary.default,
    textAlign: "left",
    opacity: isDisabled ? 0.5 : 1,
    cursor: isLoading || isDisabled ? "not-allowed!important" : "pointer!important",
    width: "fill-available",

    "&:hover": {
      background: !isLoading ? theme.surface.main.secondary.hover : "default",
    },
  };
});

const TokenIcon = ({ symbol, isLoading }: { symbol: Token; isLoading: boolean }) => {
  const tokenList = useTokenListByChain();
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const showImage = !isLoading && imageLoaded;
  const size = 28;
  const iconUrl = getTokenIcon(symbol, tokenList);

  const IconSkeleton = () => (
    <SkeletonCircle
      style={showImage ? { display: "none" } : undefined}
      width={showImage ? 0 : 28}
      height={showImage ? 0 : 28}
    />
  );

  if (tokenList == null) {
    return <IconSkeleton />;
  }

  if (iconUrl == null) {
    return null;
  }

  return (
    <>
      <img
        src={iconUrl}
        onLoad={() => setImageLoaded(true)}
        alt={`${symbol} icon`}
        style={{
          ...(showImage ? {} : { display: "none" }),
          width: size,
          height: size,
          borderRadius: "50%",
        }}
      />
      <IconSkeleton />
    </>
  );
};

const SubContainer = styled.div(({ theme }) => {
  const themeDevice = useIsDesktop() ? "Desktop" : "Mobile";
  return {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flexGrow: 1,
    marginLeft: 7,
    font: theme[themeDevice].Paragraph.Medium,
  };
});

const BalanceDetailsContainer = styled.div(({ theme }) => {
  return {
    display: "flex",
    gap: 4,
    alignItems: "center",
    color: theme.font.secondary.default,
  };
});

const TokenStats = ({
  symbol,
  isLoading,
  balanceUsd,
}: {
  symbol: Token;
  isLoading: boolean;
  balanceUsd: Decimal;
}) => {
  const chain = GlobalStore.useState(s => s.network);
  const tokenList = useTokenListByChain();
  const address = getTokenAddressBySymbol(tokenList, chain, symbol);
  const { data: tokenlistMetadata, isError } = useTokenlistMetadataByChain({ chainId: chain.id });

  if (tokenList == null || tokenlistMetadata == null || isError) return null;

  const tokenMetadata = tokenlistMetadata[address];
  if (tokenMetadata == null) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        textAlign: "right",
        alignItems: "end",
      }}
    >
      {tokenMetadata.p && (
        <Text
          textStyle="Title"
          size="Small"
          shouldScale={true}
          textCss={css`
            visibility: ${balanceUsd.isZero() ? "hidden" : "none"};
          `}
        >
          ${balanceUsd.toFixed(2)}
        </Text>
      )}
      {tokenMetadata.c != null && (
        <PercentageChange isLoading={isLoading} pctChange={tokenMetadata.c} />
      )}
    </div>
  );
};

const PercentageChange = ({ isLoading, pctChange }: { isLoading: boolean; pctChange: number }) => {
  if (isLoading) {
    return (
      <div style={{ marginBottom: 3 }}>
        <SkeletonBlock width={49} height={14} style={{ maxWidth: 49, maxHeight: 14 }} />
      </div>
    );
  }

  const isPositive = pctChange >= 0;
  return (
    <PercentageChangeContainer isPositive={isPositive}>
      {isPositive ? "+" : "-"}
      {Math.abs(pctChange).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
      %
    </PercentageChangeContainer>
  );
};

const PercentageChangeContainer = styled.div<{ isPositive: boolean }>(({ theme, isPositive }) => {
  const themeDevice = useIsDesktop() ? "Desktop" : "Mobile";
  return {
    font: theme[themeDevice].Title.XS,
    color: isPositive ? theme["Colour ramps"].Green[600] : theme["Colour ramps"].Coral[500],
  };
});

// Helpers

function getDisplayBalance(
  amount: Amount | undefined,
  symbol: Token,
  tokenList: TokenList | undefined
) {
  if (amount == null || amount.value.eq("0")) {
    return "";
  }
  return `${amount.toDisplay(symbol, tokenList)} ${symbol}`;
}
